import type { LatLng } from '@/features/detection/domain/geometry'
import { bearingAt, latLngOf, localOf, project, type RoutePolyline } from './pathGeometry'

// Progression estimée le long du parcours prévu, sans jamais toucher aux statuts des résidences : passer dans une rue ne
// termine aucun contrat, une résidence sautée reste à faire. La projection n'est permise que dans une tolérance
// crédible (précision annoncée comprise) et dans une fenêtre autour de la dernière position le long du parcours.

/** Au-delà, la position ne fait pas avancer la progression (incertaine). */
export const PROGRESS_MAX_ACCURACY_M = 35
/** Recul toléré le long du parcours (marche arrière, demi-tour court). */
export const PROGRESS_BACK_WINDOW_M = 30
/** Avance minimale de la fenêtre; elle s'élargit avec le temps écoulé. */
export const PROGRESS_AHEAD_WINDOW_M = 80
/** Positions hors fenêtre consécutives avant de rechercher le parcours ailleurs. */
export const PROGRESS_REACQUIRE_HITS = 3
/** Positions projetées consécutives avant de compter la portion comme parcourue. */
export const PROGRESS_CONFIRM_HITS = 2

export interface ProgressFix {
  lat: number
  lng: number
  accuracyM: number | null
  at: number
}

export interface ProgressState {
  /** Dernière position projetée le long du parcours; `null` : pas encore accrochée. */
  along: number | null
  /** Portion parcourue avec certitude (jamais réduite). */
  travelled: number
  hits: number
  offHits: number
  lastAt: number | null
  /** Sens de déplacement le long du parcours (hystérésis de 15 m). */
  direction: 'FORWARD' | 'BACKWARD' | null
  directionAnchor: number | null
}

export interface ProgressResult {
  state: ProgressState
  /** Position affichable sur le parcours; `null` : véhicule hors tolérance ou position incertaine. */
  snapped: { point: LatLng; along: number; bearing: number } | null
  uncertain: boolean
}

export const initialProgress: ProgressState = {
  along: null,
  travelled: 0,
  hits: 0,
  offHits: 0,
  lastAt: null,
  direction: null,
  directionAnchor: null,
}

/** Tolérance de projection : précision annoncée + marge de chaussée, bornée (jamais plus de 35 m). */
export function snapTolerance(accuracyM: number | null): number {
  return Math.max(12, Math.min(35, (accuracyM ?? 20) * 1.2 + 8))
}

export function stepProgress(polyline: RoutePolyline, state: ProgressState, fix: ProgressFix): ProgressResult {
  if (fix.accuracyM === null || fix.accuracyM > PROGRESS_MAX_ACCURACY_M) {
    return { state, snapped: null, uncertain: true }
  }
  const local = localOf(polyline, fix)
  const tolerance = snapTolerance(fix.accuracyM)
  const elapsed = state.lastAt === null ? 0 : Math.max(0, (fix.at - state.lastAt) / 1000)
  let candidate = null
  if (state.along !== null) {
    // Fenêtre : jusqu'à ~20 m/s depuis le dernier relevé, jamais tout le parcours.
    const ahead = PROGRESS_AHEAD_WINDOW_M + Math.min(elapsed, 60) * 20
    const inWindow = project(polyline, local, state.along - PROGRESS_BACK_WINDOW_M, state.along + ahead)
    if (inWindow && inWindow.distance <= tolerance) candidate = inWindow
  }
  const reacquire = state.along === null || (candidate === null && state.offHits + 1 >= PROGRESS_REACQUIRE_HITS)
  if (candidate === null && reacquire) {
    const anywhere = project(polyline, local)
    if (anywhere && anywhere.distance <= tolerance) candidate = anywhere
  }
  if (candidate === null) {
    return {
      state: { ...state, hits: 0, offHits: state.offHits + 1, lastAt: fix.at },
      snapped: null,
      uncertain: false,
    }
  }
  const hits = state.hits + 1
  const travelled = hits >= PROGRESS_CONFIRM_HITS ? Math.max(state.travelled, candidate.along) : state.travelled
  let { direction, directionAnchor } = state
  if (directionAnchor === null || reacquire) {
    directionAnchor = candidate.along
  } else if (candidate.along - directionAnchor >= 15) {
    direction = 'FORWARD'
    directionAnchor = candidate.along
  } else if (directionAnchor - candidate.along >= 15) {
    direction = 'BACKWARD'
    directionAnchor = candidate.along
  }
  return {
    state: { along: candidate.along, travelled, hits, offHits: 0, lastAt: fix.at, direction, directionAnchor },
    snapped: { point: latLngOf(polyline, candidate.point), along: candidate.along, bearing: bearingAt(polyline, candidate.along) },
    uncertain: false,
  }
}
